import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const PurchaseOrderList = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    supplier_id: '',
    date_from: '',
    date_to: ''
  });
  const [suppliers, setSuppliers] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const history = useHistory();

  useEffect(() => {
    fetchPurchaseOrders();
    fetchSuppliers();
  }, [filters, pagination.current_page]);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.current_page,
        per_page: pagination.per_page
      };
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}purchase-orders`, { params });
      setPurchaseOrders(response.data.data.data);
      setPagination({
        current_page: response.data.data.current_page,
        last_page: response.data.data.last_page,
        per_page: response.data.data.per_page,
        total: response.data.data.total
      });
      setStatuses(response.data.statuses);
    } catch (error) {
      toast.error('Error fetching purchase orders');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}suppliers`);
      setSuppliers(response.data.data.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleStatusChange = async (poId, newStatus) => {
    try {
      let endpoint = '';
      let message = '';

      switch (newStatus) {
        case 'submit':
          endpoint = `${process.env.REACT_APP_API_URL}purchase-orders/${poId}/submit`;
          message = 'Purchase order submitted successfully';
          break;
        case 'approve':
          endpoint = `${process.env.REACT_APP_API_URL}purchase-orders/${poId}/approve`;
          message = 'Purchase order approved successfully';
          break;
        case 'cancel':
          endpoint = `${process.env.REACT_APP_API_URL}purchase-orders/${poId}/cancel`;
          message = 'Purchase order cancelled successfully';
          break;
        default:
          return;
      }

      await axios.post(endpoint);
      toast.success(message);
      fetchPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}purchase-orders/${selectedPO.purchase_order_id}`);
      toast.success('Purchase order deleted successfully');
      setDeleteModal(false);
      setSelectedPO(null);
      fetchPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting purchase order');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      draft: 'bg-secondary',
      pending: 'bg-warning',
      approved: 'bg-info',
      ordered: 'bg-primary',
      partially_received: 'bg-warning',
      received: 'bg-success',
      cancelled: 'bg-danger'
    };

    return (
      <span className={`badge ${statusColors[status] || 'bg-secondary'}`}>
        {statuses[status] || status}
      </span>
    );
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= pagination.last_page; i++) {
      pages.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center">
        <div>
          Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
          {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
          {pagination.total} entries
        </div>
        <div>
          <button
            className="btn btn-primary btn-sm"
            disabled={pagination.current_page === 1}
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
          >
            Previous
          </button>
          {pages.map(page => (
            <button
              key={page}
              className={`btn btn-sm mx-1 ${page === pagination.current_page ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPagination(prev => ({ ...prev, current_page: page }))}
            >
              {page}
            </button>
          ))}
          <button
            className="btn btn-primary btn-sm"
            disabled={pagination.current_page === pagination.last_page}
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="purchase-order-list">
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Purchase Orders</h4>
          <button
            className="btn btn-primary"
            onClick={() => history.push('/purchase-orders/create')}
          >
            Create Purchase Order
          </button>
        </div>
        <div className="card-body">
          {/* Filters */}
          <div className="row mb-3">
            <div className="col-md-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search PO number..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                {Object.entries(statuses).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.supplier_id}
                onChange={(e) => handleFilterChange('supplier_id', e.target.value)}
              >
                <option value="">All Suppliers</option>
                {suppliers.map(supplier => (
                  <option key={supplier.supplier_id} value={supplier.supplier_id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <input
                type="date"
                className="form-control"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                placeholder="From Date"
              />
            </div>
            <div className="col-md-2">
              <input
                type="date"
                className="form-control"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                placeholder="To Date"
              />
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setFilters({
                    search: '',
                    status: '',
                    supplier_id: '',
                    date_from: '',
                    date_to: ''
                  });
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Supplier</th>
                  <th>Order Date</th>
                  <th>Expected Delivery</th>
                  <th>Status</th>
                  <th>Total Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center">Loading...</td>
                  </tr>
                ) : purchaseOrders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">No purchase orders found</td>
                  </tr>
                ) : (
                  purchaseOrders.map(po => (
                    <tr key={po.purchase_order_id}>
                      <td>{po.po_number}</td>
                      <td>{po.supplier_name}</td>
                      <td>{new Date(po.order_date).toLocaleDateString()}</td>
                      <td>{new Date(po.expected_delivery_date).toLocaleDateString()}</td>
                      <td>{getStatusBadge(po.status)}</td>
                      <td>${po.total_amount}</td>
                      <td>
                        <button
                          className="btn btn-info btn-sm me-1"
                          onClick={() => history.push(`/purchase-orders/${po.purchase_order_id}`)}
                        >
                          View
                        </button>
                        {po.status === 'draft' && (
                          <>
                            <button
                              className="btn btn-warning btn-sm me-1"
                              onClick={() => history.push(`/purchase-orders/${po.purchase_order_id}/edit`)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-success btn-sm me-1"
                              onClick={() => handleStatusChange(po.purchase_order_id, 'submit')}
                            >
                              Submit
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => {
                                setSelectedPO(po);
                                setDeleteModal(true);
                              }}
                            >
                              Delete
                            </button>
                          </>
                        )}
                        {po.status === 'pending' && (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleStatusChange(po.purchase_order_id, 'approve')}
                          >
                            Approve
                          </button>
                        )}
                        {['draft', 'pending', 'approved'].includes(po.status) && (
                          <button
                            className="btn btn-danger btn-sm ms-1"
                            onClick={() => handleStatusChange(po.purchase_order_id, 'cancel')}
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && renderPagination()}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete purchase order "{selectedPO?.po_number}"?
                This action cannot be undone.
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderList; 